/**
 * The generator mirror: which calendar date every occurrence of an assigned plan lands on.
 *
 * This is not a preview convenience. The trainer reviews the dates computed here and may move a
 * single occurrence; the move is submitted as a STRUCTURAL COORDINATE — cycle, phase order, day
 * order, session order — and the backend regenerates its own walk and **400s any coordinate it did
 * not itself produce** (`PlanAssignmentProcessor.ResolveOccurrenceOverrides`). So this file is one
 * half of a two-runtime algorithm, and the only thing keeping the halves together is
 * `scheduleDateParity.test.ts` replaying the fixture the backend emits.
 *
 * ── Ported from `move-with-me-frontend/.../plans/assign/model/assignSchedule.ts` ─────────────
 * Near-verbatim, deliberately. Execution ADR §1: "replicate the mobile flow verbatim … every place
 * this app invents behaviour is a place the two clients can disagree about the same plan." The
 * arithmetic, the walk order and the coordinate shape are byte-equivalent; the fixture proves it.
 *
 * These are ALL the changes made on the way over. The list is exhaustive on purpose — an incomplete
 * one asserts that everything unlisted is identical:
 *
 * 1. **`deriveDayName` is dropped.** Mobile falls back to a bare English weekday table
 *    (`'Monday'`, `` `Day ${order}` ``) when a day carries no authored name. `model/` here holds no
 *    sentences and no language: the occurrence carries the authored `dayName` or `null` plus its
 *    `dayOfWeek`, and `assignReview.ts` composes the label with the caller's `t`. A day label is a
 *    rendering decision, and mobile's own comment already says "UI localises via i18n keys".
 * 2. **`OccurrenceOverrideValue.time` is narrowed to `string | null`** rather than optional. The
 *    dialog always knows whether a row carries a time; `undefined` and `null` meaning the same
 *    thing is how the two get compared with `!=` and diverge.
 * 3. **`buildOccurrenceOverridesPayload` moved out**, to `assignRequest.ts`, with the rest of the
 *    request assembly. It is not schedule maths — it is what leaves on the wire — and keeping this
 *    module to the generator mirror is what lets the parity test say it replays the whole of it.
 * 4. **RN-isms removed** — no `~/shared/lib` barrel note, no re-export of the date primitives.
 * 5. **The occurrence carries no DURATION.** Mobile's does, because its review step tests each
 *    session against the trainer's existing appointments and needs a length to overlap with. This
 *    app makes no conflict read, so a duration would be a field nothing reads and everything has to
 *    keep filling in. It comes back with the conflict check, if that is ever built.
 *
 * ── The coordinate system, restated because it is where the 400s come from ───────────────────
 * · `cycleIndex` — 1-based and **ABSOLUTE across every phase**: a calendar week. Phase 1 owns cycles
 *   1..c1, phase 2 owns c1+1..c1+c2. A client computing it per-phase targets the wrong cycle the
 *   moment a plan has two phases, and a one-phase plan hides that completely.
 * · `phaseOrder` / `dayOrder` / `sessionOrder` — the `order` fields off the templates, as stored.
 *   Never an array index.
 *
 * ── The date formula ─────────────────────────────────────────────────────────────────────────
 * `ScheduleGenerator.ComputeDateBased`:
 *
 *     date = startDate + (absoluteCycle - 1) × 7 days + (targetIso - startIso) days
 *
 * where `targetIso` is the day's `dayOfWeek` and `startIso` the start date's weekday. The second
 * term is SIGNED: a Monday day on a Wednesday start lands two days BEFORE the start date in cycle
 * one, which is exactly what the backend generates and is why the fixture pins that case.
 *
 * It is the ONLY formula since 16 August 2026. A second one mirrored `ComputeSequenceBased`, tiling
 * a phase by Σ (1 + restDaysAfter) over its ordered days; the backend deleted the sequence engine
 * and `SchedulingMode` has one member left, so there is nothing on the wire it could compute for.
 *
 * No React, no DOM, no query hook, no i18next: this is `model/`.
 */

import { addDays, atMidnight, isoDayOfWeek } from '~/shared/lib/calendarDate';

/** The structural slice of a session template the schedule maths reads. */
export interface ScheduleSession {
  /** `SessionTemplate.Order`. */
  readonly order: number;
  /** Authored session name. DATA, never i18n. Empty string when the wire carried none. */
  readonly name: string;
}

/**
 * Which CYCLES a day runs in — `PlanDayTemplate`'s per-cycle lifespan
 * (`self-paced-plan-requirements.md` R18, training-plan R19), as authored.
 *
 * **Every number here is a PLAN-ABSOLUTE cycle index**, on the same 1-based scale as
 * `CycleSlot.cycleIndex` — not an offset into the day's phase. The backend's `CycleLifespan.Narrow`
 * compares each bound against the phase span's own absolute cycles, so a day authored "from cycle 5"
 * inside a phase opening at cycle 4 sits out that phase's first cycle and no other.
 *
 * An absent end means "wherever the phase's own end is", which is why both are nullable and why an
 * omitted bound WIDENS rather than narrows — the inversion `entities/plan-draft`'s AGENTS.md warns
 * about on the write side, and the reason this shape is a required field rather than an optional
 * one. A day that carries no window at all is `EVERY_CYCLE_OF_ITS_PHASE`, stated rather than left out.
 */
export interface DayCycleWindow {
  /** First absolute cycle the day runs in, or `null` for "from wherever its phase opens". */
  readonly activationCycle: number | null;
  /** Last absolute cycle the day runs in, or `null` for "until its phase closes". */
  readonly retirementCycle: number | null;
  /** Absolute cycles the day sits out between its two ends. Empty when it sits out none. */
  readonly excludedCycles: readonly number[];
}

/** The window of a day that runs in every cycle its phase has — and what an unwindowed day means. */
export const EVERY_CYCLE_OF_ITS_PHASE: DayCycleWindow = {
  activationCycle: null,
  retirementCycle: null,
  excludedCycles: [],
};

/** The structural slice of a day template the schedule maths reads. */
export interface ScheduleDay {
  /** `PlanDayTemplate.Order`. */
  readonly order: number;
  /** ISO 1..7 (Mon=1). `null` on a day that declares none — it inherits the start date's weekday. */
  readonly dayOfWeek: number | null;
  /**
   * Rest days following this day. Advisory only (`self-paced-plan-requirements.md` R2): no cycle
   * length, phase origin or occurrence date may be derived from it, and none is — the tiling formula
   * that once did is gone. It is READ: the self-paced review states it against the day it was
   * authored on, which is the only place it has a meaning this dialog can render.
   */
  readonly restDaysAfter: number | null;
  /** The cycles this day runs in. See `DayCycleWindow` — the numbers are plan-absolute. */
  readonly window: DayCycleWindow;
  /** Authored day name, or `null`. DATA, never i18n. */
  readonly name: string | null;
  readonly sessions: readonly ScheduleSession[];
}

/** The structural slice of a phase template the schedule maths reads. */
export interface SchedulePhase {
  /** `PlanPhaseTemplate.Order`. */
  readonly order: number;
  /** Authored phase name. DATA, never i18n. */
  readonly name: string;
  readonly cycleCount: number;
  readonly days: readonly ScheduleDay[];
}

/** The whole tree, reduced to what the generator walks. */
export interface ScheduleTree {
  /**
   * The two modes this build can name, or `null` for one it cannot. See `schedulingModeOf`: the
   * unknown answer is `null` precisely so that no future mode silently inherits the weekday formula.
   *
   * Only `'DateBased'` has dates. `'SelfPaced'` is named here so the flow can branch on it by name
   * rather than by "not DateBased" — the two refusals below mean different things and the trainer
   * hears different sentences.
   */
  readonly schedulingMode: 'DateBased' | 'SelfPaced' | null;
  readonly phases: readonly SchedulePhase[];
}

/**
 * One occurrence's structural key — the backend's `OccurrenceCoordinate`, computable before the
 * assignment (and its database ids) exists.
 */
export interface OccurrenceCoordinate {
  /** 1-based, ABSOLUTE across every phase. */
  readonly cycleIndex: number;
  readonly phaseOrder: number;
  readonly dayOrder: number;
  readonly sessionOrder: number;
}

/** One generated occurrence: where it sits in the plan, when it lands, and what it is called. */
export interface ScheduleOccurrence extends OccurrenceCoordinate {
  /** The date the backend would generate, at local midnight, before any override. */
  readonly patternDate: Date;
  /** The authored session name. Empty when none. */
  readonly sessionName: string;
  /** The authored day name, or `null` — the review model derives a label when it is null. */
  readonly dayName: string | null;
  /** ISO weekday 1..7 of the pattern date. */
  readonly dayOfWeek: number;
}

/** One cycle's bucket of occurrences, for the cycle-by-cycle review. */
export interface ScheduleCycle {
  /** 1-based, ABSOLUTE. */
  readonly cycleIndex: number;
  readonly phaseOrder: number;
  readonly phaseName: string;
  /** 1-based position within the phase — "cycle 2 of 4". */
  readonly cycleInPhase: number;
  readonly occurrences: readonly ScheduleOccurrence[];
}

/** DateBased occurrence date — mirrors `ComputeDateBased`. `absoluteCycleIndex` is 1-based. */
function computeDateBased(startDate: Date, absoluteCycleIndex: number, targetIso: number): Date {
  const dayDelta = targetIso - isoDayOfWeek(startDate);
  const weekDelta = (absoluteCycleIndex - 1) * 7;
  return addDays(atMidnight(startDate), weekDelta + dayDelta);
}

/** Ascending by `order`, non-mutating. */
function byOrder<T extends { readonly order: number }>(items: readonly T[]): T[] {
  return [...items].sort((a, b) => a.order - b.order);
}

/**
 * One cycle of the plan before any date is put on it: where it sits, and the days it runs, in
 * `Order`.
 *
 * The cycle NUMBERING lives here and nowhere else. `cycleIndex` is 1-based and absolute across every
 * phase, and getting that wrong is what sends an override to the wrong cycle on any plan with two
 * phases — so the dated walk below and the dateless self-paced review both read it from this one
 * function rather than each counting for themselves.
 */
export interface CycleSlot {
  /** 1-based, ABSOLUTE across every phase. */
  readonly cycleIndex: number;
  readonly phaseOrder: number;
  readonly phaseName: string;
  /** 1-based position within the phase — "cycle 2 of 4". */
  readonly cycleInPhase: number;
  /**
   * The days that run in THIS cycle, ascending by `Order` — the phase's days minus the ones whose
   * per-cycle lifespan sits this one out (R18). Two cycles of one phase may hold different days,
   * and may hold none at all.
   */
  readonly days: readonly ScheduleDay[];
}

/**
 * Does this day run in this absolute cycle? — the client half of `CycleLifespan.Narrow`.
 *
 * Three independent conditions, all of which must hold, and each of which is the whole of one
 * authored field: the cycle is at or after the activation (or there is none), at or before the
 * retirement (or there is none), and not one of the cycles the day sits out. The backend resolves
 * the same three against the phase's span and then normalises; normalisation cannot change
 * MEMBERSHIP, only how the ends are recorded, so a per-cycle question needs none of it.
 */
function runsInCycle(window: DayCycleWindow, cycleIndex: number): boolean {
  if (window.activationCycle !== null && cycleIndex < window.activationCycle) return false;
  if (window.retirementCycle !== null && cycleIndex > window.retirementCycle) return false;
  return !window.excludedCycles.includes(cycleIndex);
}

/**
 * Every cycle the plan runs, in phase × cycle order, with no dates and no mode check — each holding
 * only the days that ACTUALLY run in it.
 *
 * Mode-agnostic on purpose: a cycle exists in both modes and is numbered the same way in both. What
 * differs is whether it can be given a date, which is the caller's question.
 *
 * ── The per-cycle filter, and why it belongs here rather than in either caller ────────────────
 * R18: a day may run in some cycles of its phase and not others, in either mode, keyed to the cycle
 * index and never to a date. This function used to hand every cycle of a phase the SAME day array by
 * reference, so both reviews drew a plan in which cycle three was identical to cycle one — while the
 * self-paced review's own header claimed the enumeration existed precisely to show where they
 * differ. Filtering here rather than in `buildScheduleCycles` and again in `selfPacedReview` is what
 * makes one answer serve both, and it is the same reason the cycle NUMBERING lives here: two
 * spellings of a coordinate rule is how the halves drift.
 *
 * The dated walk gains the backend's own occurrence set as a side effect, which is what an override
 * coordinate is judged against. An override aimed at a filtered-out slot can no longer be authored —
 * no row renders for it — and `assignRequest` drops any that survives in state rather than refusing
 * it, so the interaction stays a non-event rather than a 400 from
 * `PlanAssignmentProcessor.ResolveOccurrenceOverrides`. Whether a trainer should be able to SEE that
 * a day is dormant in a cycle, rather than simply not see the day, is an authoring question this
 * dialog does not answer.
 */
export function planCycleSlots(tree: ScheduleTree): CycleSlot[] {
  const slots: CycleSlot[] = [];
  let absoluteCycleStart = 1;

  for (const phase of byOrder(tree.phases)) {
    const days = byOrder(phase.days);

    for (let cycleOffset = 0; cycleOffset < phase.cycleCount; cycleOffset += 1) {
      const cycleIndex = absoluteCycleStart + cycleOffset;
      slots.push({
        cycleIndex,
        phaseOrder: phase.order,
        phaseName: phase.name,
        cycleInPhase: cycleOffset + 1,
        days: days.filter((day) => runsInCycle(day.window, cycleIndex)),
      });
    }

    absoluteCycleStart += phase.cycleCount;
  }

  return slots;
}

/**
 * The whole cycle-by-cycle schedule, walked in the SAME phase × cycle × day × session order the
 * backend generator uses — which is what makes every coordinate and every date authoritative.
 */
export function buildScheduleCycles(tree: ScheduleTree, startDate: Date): ScheduleCycle[] {
  // A self-paced plan has no dates by construction — `self-paced-plan-requirements.md` R8 says the
  // calendar records what happened and never what will happen, and R13 says the assignment takes no
  // start date to anchor a formula on. So there is nothing to compute, and this is a NAMED refusal
  // rather than the unknown-mode fall-through it used to share: the two callers branch before they
  // reach here (`assignmentRequest` builds a dateless body, `useAssignFlow` builds the dateless
  // review), and this is the backstop that keeps a third caller from reading `[]` as "empty plan".
  if (tree.schedulingMode === 'SelfPaced') return [];

  // A mode this build cannot name has no formula behind it, and the weekly one is not a safe guess:
  // it would draw the trainer a calendar of dates the backend is never going to generate. No
  // schedule is the honest answer, and the review renders as the empty plan it cannot describe.
  if (tree.schedulingMode !== 'DateBased') return [];

  return planCycleSlots(tree).map((slot) => {
    const occurrences: ScheduleOccurrence[] = [];

    for (const day of slot.days) {
      // A day declaring no weekday inherits the START DATE's weekday. That is the backend's own
      // fallback, and the fixture's `date-based-day-without-weekday-uses-start-weekday` vector is
      // what stops it drifting to "Monday" or to the array index.
      const targetIso = day.dayOfWeek ?? isoDayOfWeek(startDate);
      const patternDate = computeDateBased(startDate, slot.cycleIndex, targetIso);

      for (const session of byOrder(day.sessions)) {
        occurrences.push({
          cycleIndex: slot.cycleIndex,
          phaseOrder: slot.phaseOrder,
          dayOrder: day.order,
          sessionOrder: session.order,
          patternDate,
          sessionName: session.name,
          dayName: day.name,
          dayOfWeek: targetIso,
        });
      }
    }

    return {
      cycleIndex: slot.cycleIndex,
      phaseOrder: slot.phaseOrder,
      phaseName: slot.phaseName,
      cycleInPhase: slot.cycleInPhase,
      occurrences,
    };
  });
}

/**
 * A coordinate as a stable string — the key every override map and every rendered row is addressed
 * by. Order matters and is the coordinate's own declaration order, so two keys compare as the
 * backend compares two coordinates.
 */
export function coordinateKey(coordinate: OccurrenceCoordinate): string {
  return `${String(coordinate.cycleIndex)}:${String(coordinate.phaseOrder)}:${String(
    coordinate.dayOrder,
  )}:${String(coordinate.sessionOrder)}`;
}

/**
 * An occurrence narrowed to its coordinate alone.
 *
 * A `ScheduleOccurrence` IS an `OccurrenceCoordinate` structurally, so passing one where a
 * coordinate is expected type-checks — and then the pattern date, the labels and the duration ride
 * along into whatever holds it. The refusal that names a coordinate is the case that matters: it is
 * compared and rendered, and four extra fields make it neither.
 */
export function toCoordinate(occurrence: OccurrenceCoordinate): OccurrenceCoordinate {
  return {
    cycleIndex: occurrence.cycleIndex,
    phaseOrder: occurrence.phaseOrder,
    dayOrder: occurrence.dayOrder,
    sessionOrder: occurrence.sessionOrder,
  };
}

/** Every occurrence in a schedule, flattened in generation order. */
export function flattenOccurrences(cycles: readonly ScheduleCycle[]): ScheduleOccurrence[] {
  return cycles.flatMap((cycle) => [...cycle.occurrences]);
}
